// screens/create-product-mgt/AllocateAddon.jsx
import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Table, Card, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import Select from 'react-select';
import makeAnimated from "react-select/animated";
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axios';import { TrashFill } from 'react-bootstrap-icons';

// ─── FIXES IN THIS FILE ──────────────────────────────────────────────────────
//
// FIX A: Removed the "Add Another Addon?" modal that fired after every save.
//         After saving, we now: show a toast + reset the form inline.
//         The user naturally continues by either adding another or clicking
//         "Save & Continue" in the footer — no modal interruption.
//
// FIX B: "Proceed" button is now ALWAYS rendered in the footer.
//         Previously it was hidden behind allocatedAddons.length !== 0.
//         Now: always visible. Disabled (with tooltip) until at least 1 addon
//         is saved. "Skip" becomes a clearly secondary tertiary action.
//
// FIX C: Button hierarchy in footer:
//         LEFT  → ← Back (outline-secondary, small)
//         CENTER→ Skip add-ons (text/link style — de-emphasised)
//         RIGHT → [Save This Addon] primary | [Save & Continue →] success
//
//         "Save This Addon" only shows when the user has chosen an addon.
//         "Save & Continue" is always present and clearly the main CTA.
//
// ─────────────────────────────────────────────────────────────────────────────

const AllocateAddon = ({ data, onNext, onBack, updateData, currentStep, isSubmitting, setIsSubmitting, setErrors }) => {

  const accessToken = localStorage.getItem('authToken');

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      width: "100%",
      borderRadius: "6px",
      minWidth: "200px",
      borderColor: state.isFocused ? "#80bdff" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
      "&:hover": { borderColor: state.isFocused ? "#80bdff" : "#ced4da" }
    }),
    placeholder: (provided) => ({ ...provided, color: "#6c757d" })
  };

  const [isLoading, setIsLoading]         = useState(false);
  const [isSaving, setIsSaving]           = useState(false);
  const [addonsList, setAddonsList]       = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [outlets, setOutlets]             = useState([]);

  // FIX A: removed showAddMoreModal state — no longer needed
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [allocatedAddons, setAllocatedAddons] = useState([]);
  const [currentAddon, setCurrentAddon] = useState({
    selectedAddon: null,
    selectedExcludedValues: [],
    selectedExcludedOutlets: [],
    allocatedValues: [],
    addonValues: []
  });

  const animatedComponents = makeAnimated();
  const productData = data.product || {};
  const productId   = productData._id;

  const [showEditModal, setShowEditModal]         = useState(false);
  const [editFormData, setEditFormData]           = useState({
    _id: null, product: null, addon: null,
    excludedValues: [], excludedOutlets: [], groupStatus: "ACTIVE", values: []
  });
  const [isEditBtnLoading, setIsEditBtnLoading]   = useState(false);
  const [selectedExcludedOption, setSelectedExcludedOption] = useState(null);

  useEffect(() => {
    getAddonsList();
    getOutlets();
    if (data.addons?.allocatedAddons) setAllocatedAddons(data.addons.allocatedAddons);
  }, []);

  useEffect(() => {
    if (addonsList.length > 0) {
      const allocatedIds = allocatedAddons.map(a => a.addonId);
      setAvailableAddons(addonsList.filter(addon => !allocatedIds.includes(addon._id)));
    }
  }, [addonsList, allocatedAddons]);

  useEffect(() => {
    if (currentAddon.selectedAddon) {
      const addon = addonsList.find(a => a._id === currentAddon.selectedAddon.value);
      if (addon?.addonValues) {
        const initialAllocatedValues = addon.addonValues.map(av => ({
          _id: av._id,
          value: { value: av._id, label: `${av.sequenceNumber}. ${av.value}` },
          price: '', subLabel: '', isDefault: false, excludedOutlets: []
        }));
        setCurrentAddon(prev => ({
          ...prev, addonValues: addon.addonValues,
          allocatedValues: initialAllocatedValues,
          selectedExcludedValues: [], selectedExcludedOutlets: []
        }));
      }
    }
  }, [currentAddon.selectedAddon]);

  const getAddonsList = async () => {
    setIsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axiosInstance.get("addons?page=1&limit=100", { headers });
      if (response?.data?.status) setAddonsList(response?.data?.data?.addons || []);
    } catch {
      toast.error("Failed to fetch addons", { position: "top-right", autoClose: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  const getOutlets = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axiosInstance.get("outlet", { headers });
      if (response?.data?.status === true) {
        setOutlets(response.data.data.outlets.filter(o => o.status === "ACTIVE"));
      }
    } catch {
      toast.error("Failed to load outlets", { position: "top-right", autoClose: 3000 });
    }
  };

  const handleAddonChange = (selectedOption) => {
    setCurrentAddon(prev => ({
      ...prev, selectedAddon: selectedOption,
      selectedExcludedValues: [], selectedExcludedOutlets: []
    }));
  };

  const handleValuePriceChange    = (id, price)     => setCurrentAddon(prev => ({ ...prev, allocatedValues: prev.allocatedValues.map(v => v.value.value === id ? { ...v, price } : v) }));
  const handleValueSubLabelChange = (id, subLabel)  => setCurrentAddon(prev => ({ ...prev, allocatedValues: prev.allocatedValues.map(v => v.value.value === id ? { ...v, subLabel } : v) }));
  const handleDefaultChange       = (id)            => setCurrentAddon(prev => ({ ...prev, allocatedValues: prev.allocatedValues.map(v => ({ ...v, isDefault: v.value.value === id })) }));
  const handleValueExcludedOutlets = (id, selected) => setCurrentAddon(prev => ({ ...prev, allocatedValues: prev.allocatedValues.map(v => v.value.value === id ? { ...v, excludedOutlets: selected || [] } : v) }));
  const handleExcludedValueChange  = (selected)     => setCurrentAddon(prev => ({ ...prev, selectedExcludedValues: selected || [] }));
  const handleExcludedOutletChange = (selected)     => setCurrentAddon(prev => ({ ...prev, selectedExcludedOutlets: selected || [] }));

  const validateCurrentAddon = () => {
    if (!currentAddon.selectedAddon) {
      toast.error('Please select an addon group', { position: 'top-right' });
      return false;
    }
    const excludedIds = currentAddon.selectedExcludedValues.map(v => v.value);
    const filtered    = currentAddon.allocatedValues.filter(v => !excludedIds.includes(v.value.value));
    if (!filtered.some(v => v.price && parseFloat(v.price) > 0)) {
      toast.error('Please set a price for at least one addon value', { position: 'top-right' });
      return false;
    }
    return true;
  };

  const resetCurrentAddon = () => {
    setCurrentAddon({
      selectedAddon: null, selectedExcludedValues: [],
      selectedExcludedOutlets: [], allocatedValues: [], addonValues: []
    });
  };

  const saveCurrentAddon = async () => {
    if (!validateCurrentAddon()) return;

    const excludedIds = currentAddon.selectedExcludedValues.map(v => v.value);
    const filtered    = currentAddon.allocatedValues.filter(v => !excludedIds.includes(v.value.value));

    const payload = {
      product: productData._id,
      addon: currentAddon.selectedAddon.value,
      excludedOutlets: currentAddon.selectedExcludedOutlets.map(o => o.value),
      excludedValues: excludedIds,
      status: "ACTIVE",
      values: filtered.map(val => ({
        value: val.value.value,
        price: parseFloat(val.price) || 0,
        subLabel: val.subLabel || '',
        status: "ACTIVE",
        isDefault: val.isDefault || false,
        excludedOutlets: val.excludedOutlets?.map(o => o.value) || []
      }))
    };

    Object.keys(payload).forEach(k => {
      if (Array.isArray(payload[k]) && payload[k].length === 0) delete payload[k];
    });
    payload.values = payload.values.map(v => {
      if (v.excludedOutlets?.length === 0) delete v.excludedOutlets;
      return v;
    });

    setIsSaving(true);
    setIsSubmitting(true);
    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
      const response = await axiosInstance.post('addon-product-allocation', payload, { headers });

      if (response?.data?.status) {
        const newAllocated = {
          _id: response.data.data?._id,
          allocationId: response.data.data?._id,
          addonId: currentAddon.selectedAddon.value,
          addonName: currentAddon.selectedAddon.label,
          payload,
          allocatedValues: filtered,
          excludedValues: currentAddon.selectedExcludedValues,
          excludedOutlets: currentAddon.selectedExcludedOutlets
        };
        setAllocatedAddons(prev => [...prev, newAllocated]);

        // FIX A: Reset form inline, no modal. Toast confirms success.
        resetCurrentAddon();
        toast.success(
          `"${currentAddon.selectedAddon.label}" added. Choose another or click Save & Continue.`,
          { position: 'top-right', autoClose: 3000 }
        );
      } else {
        toast.error(response?.data?.error || 'Failed to allocate addon', { position: 'top-right' });
      }
    } catch (error) {
      let msg = 'Failed to allocate addon';
      if (error.response) {
        const d = error.response.data;
        msg = d?.error?.message || d?.data?.message || d?.error || msg;
      }
      toast.error(msg, { position: "top-right", autoClose: 3000 });
      setErrors([msg]);
    } finally {
      setIsSaving(false);
      setIsSubmitting(false);
    }
  };

  // FIX B: Always-available proceed handler
  const handleProceedToNext = () => {
    updateData('addons', {
      allocatedAddons, totalAddons: allocatedAddons.length,
      isAllocated: allocatedAddons.length > 0, skipped: false
    });
    onNext();
  };

  const confirmSkipAddons = () => {
    setShowConfirmModal(false);
    updateData('addons', { allocatedAddons: [], totalAddons: 0, isAllocated: false, skipped: true });
    toast.info('Add-ons skipped. You can add them from the product edit page.', {
      position: "top-right", autoClose: 3000
    });
    onNext();
  };

  const removeAllocatedAddon = async (index) => {
    const toRemove = allocatedAddons[index];
    if (!toRemove.allocationId) {
      toast.error('Cannot delete: missing allocation ID', { position: 'top-right' });
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axiosInstance.delete(`addon-product-allocation?_id=${toRemove.allocationId}`, { headers });
      if (response?.data?.status === true) {
        setAllocatedAddons(prev => prev.filter((_, i) => i !== index));
        toast.success('Addon removed', { position: 'top-right' });
      } else {
        toast.error('Failed to remove addon', { position: 'top-right' });
      }
    } catch {
      toast.error('Failed to remove addon', { position: 'top-right' });
    }
  };

  const updateAddon = (index, allocation) => {
    const addonOption   = { value: allocation?.addonId || allocation?.payload?.addon, label: allocation?.addonName };
    const completeAddon = addonsList.find(a => a._id === addonOption.value);
    const mappedValues  = allocation?.allocatedValues?.map(val => ({
      _id: val?._id,
      value: { value: val?.value?.value, label: val?.value?.label || val?.value?.value, _id: val?.value?.value },
      price: val?.price?.toString() || '',
      subLabel: val?.subLabel || '',
      excludedOutlets: val?.excludedOutlets?.map(o => ({ value: o.value || o._id, label: o.label || o.name })) || [],
      isDefault: val?.isDefault || false
    }));
    const excludedValueIds = allocation?.payload?.excludedValues || [];
    const excludedValues   = (completeAddon?.addonValues || [])
      .filter(av => excludedValueIds.includes(av._id))
      .map(av => ({ value: av._id, label: `${av.sequenceNumber}. ${av.value}` }));
    const excludedOutlets  = allocation?.excludedOutlets?.map(o => ({ value: o.value || o._id, label: o.label || o.name })) || [];

    setEditFormData({
      _id: allocation?.allocationId || allocation?._id,
      product: { value: productData._id, label: productData.name },
      addon: addonOption, excludedValues, excludedOutlets,
      groupStatus: "ACTIVE", values: mappedValues
    });
    setShowEditModal(true);
  };

  const handleEditValueChange = (index, field, value) => {
    const updated = [...editFormData.values];
    updated[index] = { ...updated[index], [field]: value };
    setEditFormData({ ...editFormData, values: updated });
  };

  const handleEditRemoveValue = (index) => {
    const row = editFormData.values[index];
    const valueId = row?.value?._id || row?.value?.value;
    const label   = row?.value?.label || row?.value?.value;
    if (!valueId) return;
    const updatedExcluded = [...editFormData.excludedValues];
    if (!updatedExcluded.find(o => o.value === valueId)) {
      updatedExcluded.push({ value: valueId, label });
    }
    setEditFormData({
      ...editFormData,
      values: editFormData.values.filter((_, i) => i !== index),
      excludedValues: updatedExcluded
    });
  };

  const handleEditRemoveExcludedValue = (removed) => {
    const updatedExcluded = editFormData.excludedValues.filter(o => o.value !== removed.value);
    const alreadyIn = editFormData.values.find(r => r.value && r.value.value === removed.value);
    const updatedValues = alreadyIn
      ? editFormData.values
      : [...editFormData.values, { value: removed, price: '', subLabel: '', excludedOutlets: [], isDefault: false }];
    setEditFormData({ ...editFormData, excludedValues: updatedExcluded, values: updatedValues });
  };

  const handleEditMainSelectChange = (selected, index, isGroup = false) => {
    if (isGroup) {
      setEditFormData(prev => ({ ...prev, excludedOutlets: selected || [] }));
    } else {
      const updated = [...editFormData.values];
      updated[index] = { ...updated[index], excludedOutlets: selected || [] };
      setEditFormData({ ...editFormData, values: updated });
    }
  };

  const handleEditAllocationSubmit = async (e) => {
    e.preventDefault();
    setIsEditBtnLoading(true);
    try {
      const payload = {
        _id: editFormData._id,
        product: editFormData.product.value,
        addon: editFormData.addon.value,
        excludedOutlets: editFormData.excludedOutlets.map(o => o.value),
        excludedValues: editFormData.excludedValues.map(o => o.value),
        status: editFormData.groupStatus,
        values: editFormData.values.map(val => ({
          _id: val?._id, value: val.value.value,
          price: parseFloat(val.price) || 0,
          subLabel: val.subLabel || '', status: "ACTIVE",
          excludedOutlets: val.excludedOutlets?.map(o => o.value) || [],
          isDefault: val.isDefault || false
        }))
      };
      Object.keys(payload).forEach(k => {
        if (Array.isArray(payload[k]) && payload[k].length === 0) delete payload[k];
      });
      payload.values = payload.values.map(v => {
        if (v.excludedOutlets?.length === 0) delete v.excludedOutlets;
        return v;
      });

      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
      const response = await axiosInstance.put('addon-product-allocation', payload, { headers });

      if (response?.data?.status) {
        const updated = {
          _id: response.data.data?.dbResponse?._id || editFormData._id,
          allocationId: response.data.data?.dbResponse?._id || editFormData._id,
          addonId: editFormData.addon.value, addonName: editFormData.addon.label,
          payload, allocatedValues: editFormData.values,
          excludedValues: editFormData.excludedValues, excludedOutlets: editFormData.excludedOutlets
        };
        setAllocatedAddons(prev => prev.map(a => a.allocationId === editFormData._id ? updated : a));
        toast.success('Addon updated!', { position: 'top-right', autoClose: 3000 });
        setShowEditModal(false);
      } else {
        toast.error(response?.data?.error || 'Failed to update addon', { position: 'top-right' });
      }
    } catch (error) {
      let msg = 'Failed to update addon';
      if (error.response) {
        const d = error.response.data;
        msg = d?.error?.message || d?.data?.message || d?.error || msg;
      }
      toast.error(msg, { position: "top-right", autoClose: 3000 });
    } finally {
      setIsEditBtnLoading(false);
    }
  };

  const outletOptions = outlets.map(o => ({ value: o._id, label: `${o.name} (${o.city})` }));
  const addonOptions  = availableAddons.map(a => ({ value: a._id, label: `${a.title} ${a.status === "ACTIVE" ? "✅" : "⏸️"}` }));
  const getExcludedValueOptions = () => currentAddon.addonValues.map(av => ({ value: av._id, label: `${av.sequenceNumber}. ${av.value}` }));

  // FIX B: Proceed is enabled once at least 1 addon is saved
  const canProceed = allocatedAddons.length > 0;

  return (
    <div className="allocate-addon-step">

      {/* ── Skip confirmation modal (kept — it's a destructive action) ── */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Skip add-ons?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 14 }}>
          You can add add-ons later from the product edit page.
          {allocatedAddons.length > 0 && (
            <Alert variant="warning" className="mt-3 mb-0" style={{ fontSize: 13 }}>
              <strong>Warning:</strong> {allocatedAddons.length} addon(s) already saved will remain allocated.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" size="sm" onClick={confirmSkipAddons}>
            Skip add-ons
          </Button>
        </Modal.Footer>
      </Modal>

      <h6 className="mb-1 text-primary fw-semibold">Add-ons Allocation</h6>
      <p className="text-muted mb-4" style={{ fontSize: 13 }}>
        This step is optional — you can skip it and add add-ons from the product edit page.
      </p>

      {!productData._id && (
        <Alert variant="warning" className="mb-4">
          <strong>Product not created yet.</strong> Please go back to Step 1 first.
        </Alert>
      )}

      {/* ── Allocated addons summary ─────────────────────────────────── */}
      {allocatedAddons.length > 0 && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 className="mb-0 fw-semibold">Allocated add-ons</h6>
                <small className="text-muted">Saved to this product</small>
              </div>
              <Badge bg="success" pill>{allocatedAddons.length} saved</Badge>
            </div>
            <div className="table-responsive">
              <Table size="sm" hover className="mb-0">
                <thead>
                  <tr style={{ fontSize: 12 }}>
                    <th>#</th><th>Addon</th><th>Values</th><th>Excluded</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: 12 }}>
                  {allocatedAddons.map((addon, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td><strong>{addon.addonName}</strong></td>
                      <td><Badge bg="info">{addon.allocatedValues?.length || 0} values</Badge></td>
                      <td><Badge bg="secondary">{addon.excludedValues?.length || 0} excluded</Badge></td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button variant="outline-danger"  size="sm" onClick={() => removeAllocatedAddon(i)}>Remove</Button>
                          <Button variant="outline-primary" size="sm" onClick={() => updateAddon(i, addon)}>Edit</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ── Add new addon form ───────────────────────────────────────── */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <h6 className="mb-3 fw-semibold" style={{ fontSize: 14 }}>
            {allocatedAddons.length > 0 ? 'Add another add-on' : 'Add an add-on'}
          </h6>
          <Row className="gy-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: 13 }}>Add-on group</Form.Label>
                <Select
                  options={addonOptions}
                  value={currentAddon.selectedAddon}
                  onChange={handleAddonChange}
                  placeholder={availableAddons.length > 0 ? "Select addon group…" : "All addons allocated"}
                  isLoading={isLoading}
                  isDisabled={availableAddons.length === 0}
                />
                {availableAddons.length === 0 && (
                  <Form.Text className="text-success">All available addons are allocated.</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: 13 }}>Excluded outlets <span className="text-muted fw-normal">(optional)</span></Form.Label>
                <Select
                  isMulti options={outletOptions}
                  value={currentAddon.selectedExcludedOutlets}
                  onChange={handleExcludedOutletChange}
                  components={animatedComponents}
                  placeholder="Select outlets to exclude…"
                  isDisabled={!currentAddon.selectedAddon}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: 13 }}>Excluded values <span className="text-muted fw-normal">(optional)</span></Form.Label>
                <Select
                  isMulti options={getExcludedValueOptions()}
                  value={currentAddon.selectedExcludedValues}
                  onChange={handleExcludedValueChange}
                  components={animatedComponents}
                  placeholder={currentAddon.selectedAddon ? "Select values to exclude…" : "Select addon group first"}
                  isDisabled={!currentAddon.selectedAddon}
                />
                <Form.Text className="text-muted">Excluded values will not appear for this product.</Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Configure addon values table ────────────────────────────── */}
      {currentAddon.selectedAddon && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 fw-semibold" style={{ fontSize: 14 }}>Configure values</h6>
              <Badge bg="info">{currentAddon.addonValues.length} values</Badge>
            </div>
            {isLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" size="sm" />
                <p className="mt-2 text-muted" style={{ fontSize: 13 }}>Loading values…</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table striped hover size="sm">
                  <thead>
                    <tr style={{ fontSize: 12 }}>
                      <th>Value</th><th>Status</th><th>Price (₹) *</th>
                      <th>Sub-label</th><th>Default</th><th>Exclude outlets</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontSize: 12 }}>
                    {currentAddon.allocatedValues
                      .filter(v => !currentAddon.selectedExcludedValues.some(ex => ex.value === v.value.value))
                      .map((val) => (
                        <tr key={val.value.value}>
                          <td><strong>{val.value.label}</strong></td>
                          <td>
                            <Badge bg={currentAddon.addonValues.find(av => av._id === val.value.value)?.status === "ACTIVE" ? "success" : "secondary"}>
                              {currentAddon.addonValues.find(av => av._id === val.value.value)?.status}
                            </Badge>
                          </td>
                          <td>
                            <Form.Control
                              type="number" step="0.01" size="sm"
                              value={val.price || ''}
                              onChange={e => handleValuePriceChange(val.value.value, e.target.value)}
                              placeholder="0.00" style={{ width: 110 }}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="text" size="sm"
                              value={val.subLabel || ''}
                              onChange={e => handleValueSubLabelChange(val.value.value, e.target.value)}
                              placeholder="Optional" style={{ width: 130 }}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="radio" name="defaultAddonValue"
                              checked={val.isDefault || false}
                              onChange={() => handleDefaultChange(val.value.value)}
                            />
                          </td>
                          <td>
                            <Select
                              isMulti options={outletOptions}
                              value={val.excludedOutlets || []}
                              onChange={selected => handleValueExcludedOutlets(val.value.value, selected)}
                              components={animatedComponents}
                              placeholder="None excluded"
                              styles={{ control: p => ({ ...p, minHeight: 36, fontSize: 12 }) }}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ─────────────────────────────────────────────────────────────────
          FIX C: Footer button hierarchy
          LEFT:   ← Back                   (outline-secondary)
          CENTER: Skip add-ons             (link/muted — de-emphasised)
          RIGHT:  [Save This Addon]?       (outline-primary, only if addon selected)
                  [Save & Continue →]      (success — always visible, primary CTA)
      ───────────────────────────────────────────────────────────────── */}
      <div className="mt-4 d-flex justify-content-between align-items-center">

        {/* Left */}
        <Button
          variant="outline-secondary" size="sm"
          onClick={onBack} disabled={isSaving || isSubmitting}
        >
          ← Back
        </Button>

        {/* Centre — de-emphasised skip */}
        <Button
          variant="link" size="sm"
          style={{ color: '#6c757d', textDecoration: 'none', fontSize: 13 }}
          onClick={() => setShowConfirmModal(true)}
          disabled={isSaving || isSubmitting}
        >
          Skip add-ons
        </Button>

        {/* Right — action cluster */}
        <div className="d-flex gap-2">
          {/* Only visible when an addon is staged */}
          {currentAddon.selectedAddon && (
            <Button
              variant="outline-primary" size="sm"
              onClick={saveCurrentAddon}
              disabled={isSaving || isSubmitting || !productData._id}
            >
              {isSaving
                ? <><Spinner size="sm" animation="border" className="me-1" />Saving…</>
                : 'Save this add-on'
              }
            </Button>
          )}

          {/* FIX B: Always shown, disabled until at least 1 addon saved */}
          <Button
            variant="success" size="sm"
            onClick={handleProceedToNext}
            disabled={isSaving || isSubmitting || !productData._id || !canProceed}
            title={!canProceed ? "Save at least one add-on, or use Skip" : ""}
          >
            Save & Continue →
          </Button>
        </div>
      </div>

      {/* ── Edit allocation modal (unchanged logic, tidied copy) ─────── */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Form onSubmit={handleEditAllocationSubmit}>
          <Modal.Header closeButton style={{ backgroundColor: "#f8f9fa" }}>
            <Modal.Title style={{ fontSize: 16 }}>Edit addon allocation</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "1.5rem" }}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>Product</Form.Label>
                  <Form.Control type="text" value={editFormData?.product?.label || ""} readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>Add-on</Form.Label>
                  <Form.Control type="text" value={editFormData?.addon?.label || ""} readOnly />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>Excluded outlets</Form.Label>
                  <Select
                    isMulti options={outletOptions} value={editFormData.excludedOutlets}
                    onChange={s => handleEditMainSelectChange(s, null, true)}
                    components={animatedComponents} styles={customStyles} placeholder="Select outlets…"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>Re-include excluded value</Form.Label>
                  <div className="d-flex gap-2">
                    <Select
                      options={editFormData.excludedValues} value={selectedExcludedOption}
                      onChange={setSelectedExcludedOption} placeholder="Select to restore…"
                      isClearable className="w-100"
                    />
                    <Button variant="success" size="sm" onClick={() => {
                      if (!selectedExcludedOption) return;
                      const updatedExcluded = editFormData.excludedValues.filter(o => o.value !== selectedExcludedOption.value);
                      setEditFormData({
                        ...editFormData,
                        excludedValues: updatedExcluded,
                        values: [...editFormData.values, { value: selectedExcludedOption, price: '', subLabel: '', excludedOutlets: [], isDefault: false }]
                      });
                      setSelectedExcludedOption(null);
                    }}>Add</Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {editFormData?.excludedValues?.length > 0 && (
              <div className="mt-3">
                <span className="fw-semibold" style={{ fontSize: 13 }}>Excluded values:</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem" }}>
                  {editFormData.excludedValues.map(opt => (
                    <Badge key={opt.value} bg="secondary" className="d-flex align-items-center"
                      style={{ cursor: "pointer" }} onClick={() => handleEditRemoveExcludedValue(opt)}>
                      {opt.label} <TrashFill size={11} className="ms-1" />
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <hr />
            <h6 className="mb-3" style={{ fontSize: 14 }}>Allocated values</h6>
            {editFormData?.values?.map((val, i) => (
              <React.Fragment key={i}>
                <Row className="mb-2 align-items-end">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 12 }}>Value</Form.Label>
                      <Form.Control type="text" value={val?.value?.label || ""} readOnly style={{ fontSize: 12 }} />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 12 }}>Price (₹)</Form.Label>
                      <Form.Control
                        type="number" step="0.01" value={val?.price}
                        onChange={e => handleEditValueChange(i, "price", e.target.value)}
                        placeholder="0.00" required style={{ fontSize: 12 }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 12 }}>Sub-label</Form.Label>
                      <Form.Control
                        type="text" value={val?.subLabel}
                        onChange={e => handleEditValueChange(i, "subLabel", e.target.value)}
                        placeholder="Optional" style={{ fontSize: 12 }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="text-center">
                    <Form.Check
                      type="switch" id={`edit-default-${i}`} label="Default"
                      checked={val?.isDefault || false}
                      onChange={e => {
                        const updated = editFormData.values.map((r, idx) => ({ ...r, isDefault: idx === i ? e.target.checked : false }));
                        setEditFormData({ ...editFormData, values: updated });
                      }}
                      style={{ fontSize: 12 }}
                    />
                    {editFormData?.values?.length > 1 && (
                      <Button variant="danger" size="sm" className="mt-1" onClick={() => handleEditRemoveValue(i)}>
                        <TrashFill size={13} />
                      </Button>
                    )}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 12 }}>Exclude outlets</Form.Label>
                      <Select
                        isMulti options={outletOptions} value={val.excludedOutlets}
                        onChange={s => handleEditMainSelectChange(s, i)}
                        components={animatedComponents} styles={customStyles} placeholder="None excluded"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <hr className="mt-0" />
              </React.Fragment>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" size="sm" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" size="sm" disabled={isEditBtnLoading}>
              {isEditBtnLoading
                ? <><Spinner size="sm" animation="border" className="me-1" />Updating…</>
                : "Save changes"
              }
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AllocateAddon;